<!--
  Stripe Elements integration using @stripe/stripe-js
  Replaces the deprecated vue-stripe-elements-plus package
-->
<template>
    <div class="stripe-card-wrapper">
        <div v-if="!stripe || !elements" class="stripe-loading">
            <i class="el-icon-loading"></i> Loading payment form...
        </div>
        <div
            ref="cardElement"
            class="stripe-card-element"
            v-show="stripe && elements"
        ></div>
        <div v-if="error" class="stripe-error">
            {{ error.message }}
        </div>
    </div>
</template>

<script>
import { loadStripe } from "@stripe/stripe-js";

export default {
    name: "StripeElements",
    data() {
        return {
            stripe: null,
            elements: null,
            cardElement: null,
            error: null,
            complete: false
        };
    },
    computed: {
        stripeElement() {
            return this.cardElement;
        }
    },
    async mounted() {
        await this.initializeStripe();
    },
    beforeDestroy() {
        if (this.cardElement) {
            this.cardElement.destroy();
        }
    },
    methods: {
        async initializeStripe() {
            try {
                // Get Stripe public key from Laravel config
                const stripeKey =
                    window.App?.state?.config?.services?.stripe?.key;

                if (!stripeKey) {
                    console.error(
                        "Stripe public key not found in window.App.state.config.services.stripe.key"
                    );
                    this.error = {
                        message: "Payment system configuration error"
                    };
                    return;
                }

                // Initialize Stripe
                this.stripe = await loadStripe(stripeKey);

                if (!this.stripe) {
                    throw new Error("Failed to load Stripe");
                }

                // Create Elements instance
                this.elements = this.stripe.elements();

                // Create card element
                this.cardElement = this.elements.create("card", {
                    style: {
                        base: {
                            fontSize: "16px",
                            color: "#424770",
                            "::placeholder": {
                                color: "#aab7c4"
                            }
                        },
                        invalid: {
                            color: "#9e2146"
                        }
                    }
                });

                // Mount the card element after ensuring DOM is ready
                await this.$nextTick();

                if (!this.$refs.cardElement) {
                    throw new Error("Card element container not found in DOM");
                }

                this.cardElement.mount(this.$refs.cardElement);

                // Listen for changes
                this.cardElement.on("change", this.handleChange);
                this.cardElement.on("ready", this.handleReady);
            } catch (error) {
                console.error("Error initializing Stripe:", error);
                this.error = { message: "Failed to initialize payment system" };
            }
        },

        handleChange(event) {
            this.error = event.error || null;
            this.complete = event.complete;

            // Emit change event for parent component compatibility
            // Only emit completion status, not the error since we handle error display internally
            this.$emit("change", {
                complete: event.complete,
                error: null // Don't pass error to parent to avoid duplicate display
            });
        },

        handleReady() {
            // Card element is ready
            this.$emit("ready");
        },

        async createPaymentMethod(billingDetails = null) {
            if (!this.stripe || !this.cardElement) {
                throw new Error("Stripe not initialized");
            }

            const paymentMethodData = {
                type: "card",
                card: this.cardElement
            };

            // Add billing details if provided
            if (billingDetails) {
                paymentMethodData.billing_details = billingDetails;
            }

            const { error, paymentMethod } =
                await this.stripe.createPaymentMethod(paymentMethodData);

            if (error) {
                throw new Error(error.message);
            }

            return paymentMethod;
        },

        // Legacy compatibility method for existing code
        async createToken() {
            const paymentMethod = await this.createPaymentMethod();
            return {
                id: paymentMethod.id,
                paymentMethod: paymentMethod
            };
        }
    }
};
</script>

<style scoped>
.stripe-card-wrapper {
    margin: 10px 0;
}

.stripe-loading {
    padding: 20px;
    text-align: center;
    color: #666;
}

.stripe-card-element {
    padding: 10px 12px;
    border: 1px solid #ccd0d5;
    border-radius: 4px;
    background-color: white;
    box-shadow: 0 1px 3px 0 #e6ebf1;
    transition: box-shadow 150ms ease;
}

.stripe-card-element:focus {
    box-shadow: 0 1px 3px 0 #cfd7df;
    outline: none;
}

.stripe-error {
    color: #fa755a;
    margin-top: 8px;
    font-size: 14px;
}
</style>
